import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Dashboard } from './components/Dashboard';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
// import InvitationSelector from './components/InvitationSelector'; // 已在 Header 中使用
import { RSVPTracker } from './components/RSVPTracker';
import { WebsiteTemplates } from './components/WebsiteTemplates';
import { BudgetTracker } from './components/BudgetTracker';
import { TableLayout } from './components/TableLayout';
import { GiftTracker } from './components/GiftTracker';
import { InvitePage } from './components/InvitePage';
import { QuickInviteWizard } from './components/QuickInviteWizard';
import { InvitationOverview } from './components/InvitationOverview';
import { InvitationManagement } from './components/InvitationManagement';
import { EditInvitationPage } from './components/EditInvitationPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CollaborationProvider } from './contexts/CollaborationContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import CollaborationToast from './components/CollaborationToast';
import { useCollaborationToast } from './hooks/useCollaborationToast';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { OAuthCallback } from './components/auth/OAuthCallback';
import LandingPage from './components/LandingPage';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { AuthShell } from './components/auth/AuthShell';
import { SetPasswordForm } from './components/auth/SetPasswordForm';
import PlanningTimeline from './components/PlanningTimeline';
import PricingPage from './components/PricingPage';
import { UpgradePrompt } from './components/UpgradePrompt';

import { Invitation, WeddingInvitation, Guest, RSVPResponse } from './types/invitation';
import { invitationService, rsvpService } from './lib/supabase';
import { createLogger } from './lib/logger';

const BlogListPage = lazy(() => import('./components/blog/BlogListPage'));
const BlogPostPage = lazy(() => import('./components/blog/BlogPostPage'));
const FeatureLandingPage = lazy(() => import('./components/features/FeatureLandingPage'));

const log = createLogger('app');

interface AppContentProps {
  guests: Guest[];
  setGuests: React.Dispatch<React.SetStateAction<Guest[]>>;
  allInvitations: Invitation[];
  setAllInvitations: React.Dispatch<React.SetStateAction<Invitation[]>>;
  weddingInvitations: WeddingInvitation[];
  loadingInvitations: boolean;
  rsvpRefreshTrigger: number;
  setRsvpRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
  loadInvitations: (userId: string) => Promise<void>;
  handleDeleteInvitation: (invitationId: string) => Promise<void>;
}

const loadInvitationsFromStorage = (): Invitation[] => {
  try {
    const saved = localStorage.getItem('allInvitations');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    log.error("Failed to load invitations from storage", e);
  }
  return [];
};

function AppRouter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [weddingInvitations, setWeddingInvitations] = useState<WeddingInvitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(true);
  const [rsvpRefreshTrigger, setRsvpRefreshTrigger] = useState(0);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [allInvitations, setAllInvitations] = useState<Invitation[]>(loadInvitationsFromStorage);

  // 單一首頁版本；保留簡單 CTA log
  const landing = <LandingPage onCTAClick={() => {}} />;

  const loadInvitations = async (userId: string) => {
    try {
      log.debug('開始載入邀請函，userId:', userId);
      setLoadingInvitations(true);
      const invitations = await invitationService.getAllInvitations(userId);
      log.info('邀請函載入成功，數量:', invitations.length);
      setWeddingInvitations(invitations);
    } catch (error) {
      log.error('載入邀請函失敗:', error);
      // 即使失敗也要設置空陣列，讓用戶看到歡迎畫面
      setWeddingInvitations([]);
    } finally {
      log.debug('載入完成，重置 loading 狀態');
      setLoadingInvitations(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadInvitations(user.id);
    } else {
      setLoadingInvitations(false);
    }
  }, [user?.id]);

  useEffect(() => {
    localStorage.setItem('allInvitations', JSON.stringify(allInvitations));
  }, [allInvitations]);

  // 先前的 GrowthBook 實驗已移除

  const handleRsvpSubmit = () => {
    setRsvpRefreshTrigger(prev => prev + 1);
  };

  const handleInvitationUpdate = async (updatedInvitation: WeddingInvitation) => {
    try {
      await invitationService.updateInvitation(updatedInvitation.id, updatedInvitation);
      if (user?.id) await loadInvitations(user.id);
      navigate('/dashboard');
    } catch (error) {
      log.error('更新邀請函失敗:', error);
      alert(`更新失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    try {
      await invitationService.deleteInvitation(invitationId);
      if (user?.id) await loadInvitations(user.id);
    } catch (error) {
      log.error('刪除邀請函失敗:', error);
      alert(`刪除失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  return (
    <Routes>
      <Route path="/" element={landing} />
      <Route path="/login" element={<AuthShell><LoginForm onSignUpClick={() => navigate('/register')} /></AuthShell>} />
      <Route path="/register" element={<AuthShell><RegisterForm onSignInClick={() => navigate('/login')} /></AuthShell>} />
      <Route path="/set-password" element={<ProtectedRoute><SetPasswordForm onSuccess={() => navigate('/dashboard')} onCancel={() => navigate('/dashboard')} /></ProtectedRoute>} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/features" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">載入中...</div>}><FeatureLandingPage /></Suspense>} />
      <Route path="/blog" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">載入中...</div>}><BlogListPage /></Suspense>} />
      <Route path="/blog/:slug" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">載入中...</div>}><BlogPostPage /></Suspense>} />
      <Route path="/auth/callback" element={<OAuthCallback onSuccess={() => navigate('/dashboard')} onError={(error) => { log.error(error); navigate('/login'); }} />} />
      <Route path="/invite/:code" element={<InvitePageWrapper onRsvpSubmit={handleRsvpSubmit} />} />
      <Route path="/dashboard" element={<ProtectedRoute><AppContent guests={guests} setGuests={setGuests} allInvitations={allInvitations} setAllInvitations={setAllInvitations} weddingInvitations={weddingInvitations} loadingInvitations={loadingInvitations} rsvpRefreshTrigger={rsvpRefreshTrigger} setRsvpRefreshTrigger={setRsvpRefreshTrigger} loadInvitations={loadInvitations} handleDeleteInvitation={handleDeleteInvitation} /></ProtectedRoute>} />
      <Route path="/edit/:code" element={<ProtectedRoute><EditInvitationPageWrapper onSave={handleInvitationUpdate} /></ProtectedRoute>} />
      <Route path="/timeline" element={<ProtectedRoute><PlanningTimelinePageWrapper weddingInvitations={weddingInvitations} /></ProtectedRoute>} />
    </Routes>
  );
}

function AppContent({ guests, setGuests, allInvitations, setAllInvitations, weddingInvitations, loadingInvitations, rsvpRefreshTrigger, setRsvpRefreshTrigger, loadInvitations, handleDeleteInvitation }: AppContentProps) {
  const { user, userProfile } = useAuth();
  const { canCreateInvitation, limits } = useSubscription();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'dashboard';
  const [activeModule, setActiveModule] = useState(initialTab);
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [showQuickWizard, setShowQuickWizard] = useState(false);
  const [showInvitationLimitPrompt, setShowInvitationLimitPrompt] = useState(false);
  const [showInvitationOverview, setShowInvitationOverview] = useState<string | null>(null);
  const [rsvpStats, setRsvpStats] = useState({ 
    total: 0, 
    attending: 0, 
    declined: 0, 
    totalGuests: 0,
    guestSideStats: { groom: 0, bride: 0 }
  });
  const [confirmedAttendees, setConfirmedAttendees] = useState<RSVPResponse[]>([]);
  
  // 邀請函選擇狀態 (移除 index，改用 ID)
  const [activeInvitationId, setActiveInvitationId] = useState<string | null>(null);

  // 記住選中的邀請函
  useEffect(() => {
    if (weddingInvitations.length > 0) {
      const savedId = sessionStorage.getItem('activeInvitationId');
      if (savedId) {
        const foundInvitation = weddingInvitations.find((inv: WeddingInvitation) => inv.id === savedId);
        if (foundInvitation) {
          setActiveInvitationId(savedId);
          return;
        }
      }
      // 如果沒有保存的ID或找不到，默認選中第一個
      setActiveInvitationId(weddingInvitations[0].id);
    }
  }, [weddingInvitations]);

  // 保存選中的邀請函ID並清空相關資料快取
  const handleInvitationSelect = (invitationId: string, _index: number) => {
    log.debug(`切換到邀請函: ${invitationId}`);
    
    // 清空當前資料狀態
    setRsvpStats({ 
      total: 0, 
      attending: 0, 
      declined: 0, 
      totalGuests: 0,
      guestSideStats: { groom: 0, bride: 0 }
    });
    setConfirmedAttendees([]);
    
    // 更新選中的邀請函
    setActiveInvitationId(invitationId);
    sessionStorage.setItem('activeInvitationId', invitationId);
    
    // 觸發數據重新載入
    setRsvpRefreshTrigger(prev => prev + 1);
    
    log.info('邀請函切換完成，觸發資料重新載入');
  };

  const handleQuickInvite = useCallback(() => {
    const activeCount = weddingInvitations.filter(inv => inv.isActive !== false).length;
    if (!canCreateInvitation(activeCount)) {
      setShowInvitationLimitPrompt(true);
      return;
    }
    setShowQuickWizard(true);
  }, [weddingInvitations, canCreateInvitation]);

  useEffect(() => {
    const loadRSVPData = async () => {
      if (!user?.id || weddingInvitations.length === 0) return;
      
      // 使用選中的邀請函，如果沒有選中則使用第一個
      const selectedInvitation = activeInvitationId 
        ? weddingInvitations.find((inv: WeddingInvitation) => inv.id === activeInvitationId)
        : weddingInvitations[0];
        
      if (!selectedInvitation) {
        log.debug('沒有可用的邀請函');
        return;
      }
      
      try {
        log.debug(`載入 RSVP 資料: ${selectedInvitation.inviteCode}`);
        const [stats, attendees] = await Promise.all([
          rsvpService.getRSVPStats(selectedInvitation.inviteCode, user.id),
          rsvpService.getRSVPsByInviteCode(selectedInvitation.inviteCode, user.id)
        ]);
        setRsvpStats(stats);
        setConfirmedAttendees(attendees.filter(a => a.rsvpData.attending === 'yes'));
        log.info(`RSVP 資料載入完成: ${stats.attending} 出席`);
      } catch (error) {
        log.error('載入 RSVP 資料失敗:', error);
        // 重置為空資料
        setRsvpStats({ 
          total: 0, 
          attending: 0, 
          declined: 0, 
          totalGuests: 0,
          guestSideStats: { groom: 0, bride: 0 }
        });
        setConfirmedAttendees([]);
      }
    };
    loadRSVPData();
  }, [weddingInvitations, rsvpRefreshTrigger, user?.id, activeInvitationId]);

  const handleUpdateGuest = (guestId: string, updatedGuest: Partial<Guest>) => {
    setGuests(prev => prev.map(g => g.id === guestId ? { ...g, ...updatedGuest } : g));
  };

  const handleDeleteGuest = (guestId: string) => {
    setGuests(prev => prev.filter(g => g.id !== guestId));
    setAllInvitations(prev => prev.filter(i => i.guestId !== guestId));
  };

  const handleSendInvitation = (guestIds: string[]) => {
    const newInvs = guestIds
      .filter(gid => !allInvitations.some(i => i.guestId === gid))
      .map(gid => ({ id: `inv-${gid}-${Date.now()}`, guestId: gid, inviteCode: weddingInvitations[0]?.inviteCode || 'default', status: 'sent' as const, sentAt: new Date() }));
    setAllInvitations(prev => [...prev, ...newInvs]);
  };

  const renderActiveModule = () => {
    // 使用邀請函 ID 而非陣列索引來識別邀請函（更安全）
    const activeInvitation = activeInvitationId 
      ? weddingInvitations.find((inv: WeddingInvitation) => inv.id === activeInvitationId) 
      : weddingInvitations[0] || null;
    const inviteCode = activeInvitation?.inviteCode;
    const invitationId = activeInvitation?.id;
    const weddingDate = activeInvitation?.weddingDate;
    
    // 如果選中的邀請函不存在，顯示錯誤訊息或選擇提示
    if (!activeInvitation && activeModule !== 'dashboard') {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            {activeInvitationId ? (
              <div>
                <p className="text-lg text-wedding-text-body mb-2">找不到邀請函</p>
                <p className="text-sm text-wedding-text-body mb-4">
                  可能是權限不足或邀請函已被刪除
                </p>
                <button
                  onClick={() => {
                    setActiveInvitationId(null);
                    sessionStorage.removeItem('activeInvitationId');
                  }}
                  className="px-4 py-2 bg-wedding-pink/20 text-wedding-pink rounded-lg hover:bg-wedding-pink/30 transition-colors mr-3"
                >
                  重新選擇
                </button>
              </div>
            ) : (
              <div>
                <p className="text-lg text-wedding-text-body mb-4">請先選擇或創建邀請函</p>
                <button
                  onClick={() => setShowQuickWizard(true)}
                  className="px-6 py-3 bg-wedding-pink text-white rounded-lg hover:bg-wedding-pink/90 transition-colors"
                >
                  建立邀請函
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    // 對於 dashboard 模組，允許沒有邀請函的情況
    // 對於其他功能模組，確保有必要的資料再渲染組件
    if (activeModule !== 'dashboard' && (!inviteCode || !invitationId)) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-lg text-wedding-text-body mb-4">邀請函資料載入中...</p>
            <p className="text-sm text-wedding-text-muted">請先建立邀請函</p>
          </div>
        </div>
      );
    }

    switch (activeModule) {
      case 'rsvp': return <RSVPTracker
        inviteCode={inviteCode!}
        refreshTrigger={rsvpRefreshTrigger}
        currentInvitation={activeInvitation}
      />;
      case 'website': return <WebsiteTemplates />;
      case 'budget': return <BudgetTracker />;
      case 'tables': return <TableLayout
        confirmedAttendees={confirmedAttendees}
        inviteCode={inviteCode}
        refreshTrigger={rsvpRefreshTrigger}
        currentInvitation={activeInvitation}
      />;
      case 'gifts': return <GiftTracker
        inviteCode={inviteCode!}
        invitationId={invitationId!}
        refreshTrigger={rsvpRefreshTrigger}
        currentInvitation={activeInvitation}
      />;
      case 'invitations': return <InvitationManagement guests={guests} invitations={allInvitations} inviteCode={inviteCode!} onUpdateGuest={handleUpdateGuest} onDeleteGuest={handleDeleteGuest} onSendInvitation={handleSendInvitation} />;
      case 'timeline': return <PlanningTimeline
        invitationId={invitationId ?? null}
        weddingDate={weddingDate ?? null}
        currentInvitation={activeInvitation}
      />;
      default: return <Dashboard setActiveModule={setActiveModule} onQuickInvite={handleQuickInvite} weddingInvitations={weddingInvitations} onEditInvitation={(code) => navigate(`/edit/${code}`)} onDeleteInvitation={handleDeleteInvitation} loadingInvitations={loadingInvitations} rsvpStats={rsvpStats} weddingDate={activeInvitation?.weddingDate} onRetryLoad={user?.id ? () => loadInvitations(user.id) : undefined} />;
    }
  };

  if (showInvitationOverview) {
    const current = weddingInvitations.find(wi => wi.inviteCode === showInvitationOverview);
    return current ? <InvitationOverview weddingInvitation={current} invitations={allInvitations} guests={guests} onEdit={() => { setShowInvitationOverview(null); setShowQuickWizard(true); }} onManageGuests={() => { setShowInvitationOverview(null); setActiveModule('invitations'); }} onViewInvitations={() => { setShowInvitationOverview(null); setActiveModule('invitations'); }} /> : null;
  }

  if (showQuickWizard) {
    return <QuickInviteWizard onComplete={async (wi) => { if(user?.id) await loadInvitations(user.id); setShowQuickWizard(false); setShowInvitationOverview(wi.inviteCode); }} onBack={() => setShowQuickWizard(false)} />;
  }

  return (
    <div className="flex h-screen bg-wedding-bg text-wedding-text-body">
      <Sidebar 
        activeModule={activeModule} 
        setActiveModule={setActiveModule} 
        userProfile={userProfile}
        isOpen={isSidebarOpen}
      />
      <div className="flex flex-col flex-1 overflow-hidden">
        <Header 
          activeModule={activeModule} 
          onToggleSidebar={() => setSidebarOpen(!isSidebarOpen)}
          weddingInvitations={weddingInvitations}
          activeInvitationId={activeInvitationId}
          onInvitationSelect={handleInvitationSelect}
        />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">{renderActiveModule()}</main>
      </div>
      <UpgradePrompt
        open={showInvitationLimitPrompt}
        onClose={() => setShowInvitationLimitPrompt(false)}
        message={`您的方案最多支援 ${limits.maxActiveInvitations} 張活躍邀請函。升級方案以建立更多邀請函。`}
      />
    </div>
  );
}

const InvitePageWrapper = ({ onRsvpSubmit }: { onRsvpSubmit: () => void }) => {
  const { code } = useParams<{ code: string }>();
  const [invitation, setInvitation] = useState<WeddingInvitation | null>();

  useEffect(() => {
    if (code) {
      invitationService.getInvitationByCode(code).then(setInvitation);
    }
  }, [code]);

  if (!code) return <div>無效的邀請碼</div>;
  if (invitation === undefined) return <div>載入中...</div>;
  if (!invitation) return <div>找不到邀請函</div>;

  return <InvitePage inviteCode={code} onRsvpSubmit={onRsvpSubmit} weddingInvitation={invitation} />;
}

const EditInvitationPageWrapper = ({ onSave }: { onSave: (inv: WeddingInvitation) => void }) => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invitation, setInvitation] = useState<WeddingInvitation | null>();

  useEffect(() => {
    if (code && user?.id) {
      invitationService.getInvitationByCode(code).then(inv => {
        if (inv?.userId === user.id) setInvitation(inv);
        else setInvitation(null);
      });
    }
  }, [code, user?.id]);

  if (invitation === undefined) return <div>載入中...</div>;
  if (!invitation) return <div>找不到邀請函或沒有權限</div>;

  return <EditInvitationPage invitation={invitation} onSave={onSave} onCancel={() => navigate('/dashboard')} />;
}

function PlanningTimelinePageWrapper({ weddingInvitations }: { weddingInvitations: WeddingInvitation[] }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth(); // 從 useAuth 獲取 userProfile
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const activeInvitation = weddingInvitations[0] || null;
  const activeInvitationId = activeInvitation?.id || null;
  const weddingDate = activeInvitation?.weddingDate || null;

  const handleSidebarModuleClick = (module: string) => {
    if (module === 'dashboard') {
      navigate('/dashboard');
    } else if (module === 'timeline') {
      navigate('/timeline');
    }
  };

  return (
    <div className="flex h-screen bg-wedding-bg text-wedding-text-body">
      <Sidebar 
        activeModule="timeline" 
        setActiveModule={handleSidebarModuleClick} 
        userProfile={userProfile}
        isOpen={isSidebarOpen}
      />
      <div className="flex flex-col flex-1 overflow-hidden">
        <Header 
          activeModule="timeline" 
          onToggleSidebar={() => setSidebarOpen(!isSidebarOpen)} 
        />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <PlanningTimeline invitationId={activeInvitationId} weddingDate={weddingDate} />
        </main>
      </div>
    </div>
  );
}

// 全域協作通知組件
function GlobalCollaborationToast() {
  const { messages, dismissToast } = useCollaborationToast();
  
  return <CollaborationToast messages={messages} onDismiss={dismissToast} />;
}

export function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <CollaborationProvider>
          <BrowserRouter>
            <AppRouter />
            <GlobalCollaborationToast />
          </BrowserRouter>
        </CollaborationProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}
